// §10 / §9.3 — a `Payload` on the wire.
//
// `Payload.items[].bytes` is a `Uint8Array`, and §9.3's table says it travels **base64**: "a
// structured-clone `Uint8Array` is not what every host can send". So there are two shapes of the
// same thing, and this module is the only place either is built — the client encodes, the router
// decodes, and both call the same pair, which is what stops them drifting.
import { HangarApiError, type Payload } from '@hangar/shared';

export interface WireItem {
  mime: string;
  /** base64. */
  bytes?: string;
  text?: string;
  path?: string;
}

export interface WirePayload {
  items: WireItem[];
}

/** How much a single item may carry across the bridge. Beyond this, a path belongs in `path`. */
export const MAX_PAYLOAD_ITEM_BYTES = 8 * 1024 * 1024;

export function toBase64(bytes: Uint8Array): string {
  let binary = '';
  // Chunked: `String.fromCharCode(...bytes)` on a megabyte overflows the argument list.
  const CHUNK = 0x8000;
  for (let offset = 0; offset < bytes.length; offset += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + CHUNK));
  }
  return btoa(binary);
}

export function fromBase64(value: string): Uint8Array {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return bytes;
}

/** Shell/app → the wire. */
export function wirePayloadOut(payload: Payload): WirePayload {
  return {
    items: payload.items.map((item) => {
      const wire: WireItem = { mime: item.mime };
      if (item.bytes) {
        if (item.bytes.byteLength > MAX_PAYLOAD_ITEM_BYTES) {
          throw new HangarApiError(
            'QUOTA_EXCEEDED',
            `a transfer item may not exceed ${MAX_PAYLOAD_ITEM_BYTES} bytes`,
          );
        }
        wire.bytes = toBase64(item.bytes);
      }
      if (item.text !== undefined) wire.text = item.text;
      if (item.path !== undefined) wire.path = item.path;
      return wire;
    }),
  };
}

/**
 * The wire → a `Payload`.
 *
 * §9.3's schema has already checked the shape; what is left is the decoding, and a `bytes` field
 * that is not base64 is a malformed frame rather than an exception on the message path (§9.3:
 * dropped and counted, never thrown *out*).
 */
export function wirePayloadIn(wire: unknown): Payload {
  const source = wire as WirePayload | null;
  if (!source || !Array.isArray(source.items)) {
    throw new HangarApiError('BAD_REQUEST', 'a transfer payload needs an items array');
  }
  return {
    items: source.items.map((item) => {
      const out: Payload['items'][number] = { mime: item.mime };
      if (typeof item.bytes === 'string') {
        try {
          out.bytes = fromBase64(item.bytes);
        } catch {
          throw new HangarApiError('BAD_REQUEST', `'${item.mime}' bytes are not base64`);
        }
      }
      if (typeof item.text === 'string') out.text = item.text;
      if (typeof item.path === 'string') out.path = item.path;
      return out;
    }),
  };
}
